// src/components/ui/Modal.js
import React from 'react';
import { X } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

/**
 * Reusable Modal Component
 * Provides consistent styling and structure for modals
 * Handles overlay, backdrop blur, centered positioning, and close button
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} title - Modal title text
 * @param {ReactNode} children - Modal content
 * @param {string} width - Modal width (default: '500px')
 * @param {string} height - Modal height (default: 'auto')
 * @param {string} maxHeight - Maximum modal height (default: '90vh')
 * @param {boolean} showCloseButton - Show X button in top-right (default: true)
 * @param {ReactNode} headerContent - Content above title (e.g., AnimatedCharacter)
 * @param {string} titleAlign - Title alignment: 'left' or 'center' (default: 'left')
 * @param {string} titleClassName - Additional classes for title
 * @param {string} contentPadding - Padding classes for content (default: 'px-app-4xl py-app-3xl')
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  width = '500px',
  height = 'auto',
  maxHeight = '90vh',
  showCloseButton = true,
  headerContent = null,
  titleAlign = 'left',
  titleClassName = '',
  contentPadding = 'p-app-3xl',
}) => {
  const titleAlignClass = titleAlign === 'center' ? 'text-center' : '';
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        {/* Backdrop with blur */}
        <Dialog.Overlay
          className="fixed inset-0 z-[200] bg-black/40"
          style={{
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        />

        {/* Modal Container */}
        <div className="fixed left-1/2 top-1/2 z-[201] -translate-x-1/2 -translate-y-1/2">
          {/* Close Button - positioned outside modal content */}
          {showCloseButton && (
            <Dialog.Close asChild>
              <button
                className="group absolute w-comp-3xl h-comp-3xl rounded-full border border-app-gray-900 bg-app-white hover:bg-app-gray-300 transition-colors duration-app-normal flex items-center justify-center z-10"
                title="Close"
                style={{
                  top: '-10px',
                  right: '-10px',
                  pointerEvents: 'auto'
                }}
              >
                {/* X shape */}
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  className="text-app-gray-900"
                >
                  <path 
                    d="M1 1L11 11M11 1L1 11" 
                    stroke="currentColor" 
                    strokeWidth="1" 
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </Dialog.Close>
          )}

          {/* Modal Content */}
          <Dialog.Content
            className={`relative bg-app-white rounded-app-lg border border-app-gray-900 focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${contentPadding}`}
            style={{
              width,
              height: height === 'auto' ? undefined : height,
              maxHeight: height === 'auto' ? maxHeight : undefined,
              overflow: 'auto',
              pointerEvents: 'auto',
              display: height === 'auto' ? 'block' : 'flex'
            }}
          >
            {/* Header Content (e.g., AnimatedCharacter) */}
            {headerContent && (
              <div className="mb-app-md">
                {headerContent}
              </div>
            )}

            {/* Title */}
            {title && (
              <Dialog.Title className={`text-app-2xl font-medium text-app-gray-900 mb-app-xl ${titleAlignClass} ${titleClassName}`}>
                {title}
              </Dialog.Title>
            )}

            {/* Content */}
            {children}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Modal;